import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import {
  DialogBody,
  DialogMetaBar,
  DialogMetaChip,
  DialogMetaChipAccent,
  DialogSection,
  DialogSectionTitle,
  LineItemsTable,
  LineItemsHeader,
  LineItemRow,
  LineItemArtwork,
  LineItemImage,
  LineItemDetails,
  LineItemName,
  LineItemNote,
  LineItemSize,
  LineItemQty,
  LineItemPrice,
  LineItemActions,
  DownloadArtworkButton,
  DetailsColumns,
  DetailPanel,
  DetailPanelTitle,
  DetailLine,
  DetailLineLabel,
  DetailLineValue,
  DetailTotalLine,
  DetailText,
  DetailTextMuted,
  ReceiptLink,
  EmptyLineItems,
} from "styles/pages/admin-dtf-orders";
import {
  formatOrderDate,
  formatCad,
  getOrderTotal,
  getOrderItems,
  getItemQuantity,
  getTotalItemCount,
  getOrderNumber,
  getOrderStatus,
  getPaymentStatus,
  getCardDisplay,
  formatShippingMethod,
  formatShippingAddress,
  getSizeLabel,
  getArtworkOptionLabels,
  getReceiptUrl,
  getArtworkUrl,
  downloadArtwork,
} from "pages/dtf-transfers/orders/helpers";

const getCustomerEmail = (order) =>
  order?.user ??
  order?.checkout?.contact?.email ??
  order?.checkout?.shippingAddress?.email ??
  null;

const formatStatusLabel = (status) => {
  if (!status) return "—";
  return status
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ");
};

const OrderDetailDialog = ({ open, order, onClose }) => {
  if (!order) return null;

  const items = getOrderItems(order);
  const itemCount = getTotalItemCount(items);
  const checkout = order?.checkout ?? {};
  const currency = order?.currency || checkout.currency || "CAD";
  const paymentStatus = getPaymentStatus(order);
  const orderStatus = getOrderStatus(order);
  const cardDisplay = getCardDisplay(order);
  const shippingAddress = formatShippingAddress(checkout.shippingAddress);
  const receiptUrl = getReceiptUrl(order);
  const orderNumber = getOrderNumber(order);
  const customerEmail = getCustomerEmail(order);
  const taxRatePercent =
    checkout.taxRate != null ? Math.round(checkout.taxRate * 100) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          pb: 1,
          pt: 2.5,
          px: 3,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 20,
              fontFamily: "Inter600",
              color: "#0f172a",
              lineHeight: 1.3,
            }}
          >
            Order #{orderNumber ?? "—"}
          </div>
          {customerEmail ? (
            <div
              style={{
                marginTop: 4,
                fontSize: 14,
                color: "#64748b",
              }}
            >
              {customerEmail}
            </div>
          ) : null}
        </div>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3, pt: 0 }}>
        <DialogBody>
          <DialogMetaBar>
            <DialogMetaChip>{formatOrderDate(order)}</DialogMetaChip>
            {paymentStatus !== "—" ? (
              <DialogMetaChipAccent>
                Payment: {paymentStatus}
              </DialogMetaChipAccent>
            ) : null}
            <DialogMetaChipAccent>
              Status: {formatStatusLabel(orderStatus)}
            </DialogMetaChipAccent>
          </DialogMetaBar>

          <DialogSection>
            <DialogSectionTitle>
              Line Items ({itemCount})
            </DialogSectionTitle>

            {items.length === 0 ? (
              <EmptyLineItems>No line items found for this order.</EmptyLineItems>
            ) : (
              <LineItemsTable>
                <LineItemsHeader>
                  <span>Artwork</span>
                  <span>Size</span>
                  <span>Qty</span>
                  <span style={{ textAlign: "right" }}>Price</span>
                  <span style={{ textAlign: "right" }}>Download</span>
                </LineItemsHeader>

                {items.map((item, index) => {
                  const quantity = getItemQuantity(item);
                  const sizeLabel = getSizeLabel(item);
                  const artworkUrl = getArtworkUrl(item);
                  const optionLabels = getArtworkOptionLabels(item);

                  return (
                    <LineItemRow key={item.id ?? item.cartRecordId ?? index}>
                      <LineItemArtwork>
                        <LineItemImage
                          src={item.previewUrl || item.secureUrl}
                          alt={item.fileName || "Order artwork"}
                        />
                        <LineItemDetails>
                          <LineItemName>
                            {item.fileName || "Artwork"}
                          </LineItemName>
                          <LineItemNote>
                            {sizeLabel ? `${sizeLabel} · ` : ""}
                            Qty {quantity}
                            {optionLabels.length
                              ? ` · ${optionLabels.join(" · ")}`
                              : ""}
                          </LineItemNote>
                        </LineItemDetails>
                      </LineItemArtwork>

                      <LineItemSize>{sizeLabel || "—"}</LineItemSize>
                      <LineItemQty>{quantity}</LineItemQty>
                      <LineItemPrice>
                        {formatCad(item.total, currency)}
                      </LineItemPrice>
                      <LineItemActions>
                        <DownloadArtworkButton
                          type="button"
                          disabled={!artworkUrl}
                          onClick={async (event) => {
                            event.stopPropagation();
                            await downloadArtwork(item, orderNumber);
                          }}
                        >
                          <DownloadOutlinedIcon fontSize="inherit" />
                          Download
                        </DownloadArtworkButton>
                      </LineItemActions>
                    </LineItemRow>
                  );
                })}
              </LineItemsTable>
            )}
          </DialogSection>

          <DetailsColumns>
            <DetailPanel>
              <DetailPanelTitle>
                <ReceiptLongOutlinedIcon fontSize="inherit" />
                Order Summary
              </DetailPanelTitle>

              <DetailLine>
                {checkout.rushSameDay && checkout.itemsSubtotal != null ? (
                  <>
                    <DetailLineLabel>
                      Items · {itemCount} item{itemCount === 1 ? "" : "s"}
                    </DetailLineLabel>
                    <DetailLineValue>
                      {formatCad(checkout.itemsSubtotal, currency)}
                    </DetailLineValue>
                  </>
                ) : (
                  <>
                    <DetailLineLabel>
                      Subtotal · {itemCount} item
                      {itemCount === 1 ? "" : "s"}
                      {checkout.rushSameDay ? " (Rush Same Day)" : ""}
                    </DetailLineLabel>
                    <DetailLineValue>
                      {formatCad(checkout.subtotal, currency)}
                    </DetailLineValue>
                  </>
                )}
              </DetailLine>

              {checkout.rushSameDay && checkout.itemsSubtotal != null ? (
                <>
                  <DetailLine>
                    <DetailLineLabel>Rush Same Day</DetailLineLabel>
                    <DetailLineValue>
                      {formatCad(
                        checkout.rushSameDayFee ?? checkout.itemsSubtotal,
                        currency,
                      )}
                    </DetailLineValue>
                  </DetailLine>
                  <DetailLine>
                    <DetailLineLabel>Subtotal</DetailLineLabel>
                    <DetailLineValue>
                      {formatCad(checkout.subtotal, currency)}
                    </DetailLineValue>
                  </DetailLine>
                </>
              ) : null}

              <DetailLine>
                <DetailLineLabel>
                  Shipping
                  {checkout.shippingMethod
                    ? ` (${formatShippingMethod(checkout.shippingMethod)})`
                    : ""}
                </DetailLineLabel>
                <DetailLineValue>
                  {formatCad(checkout.shippingCost, currency)}
                </DetailLineValue>
              </DetailLine>

              {checkout.taxAmount != null ? (
                <DetailLine>
                  <DetailLineLabel>
                    Tax{taxRatePercent != null ? ` (${taxRatePercent}%)` : ""}
                  </DetailLineLabel>
                  <DetailLineValue>
                    {formatCad(checkout.taxAmount, currency)}
                  </DetailLineValue>
                </DetailLine>
              ) : null}

              <DetailTotalLine>
                <DetailLineLabel>Total ({currency})</DetailLineLabel>
                <DetailLineValue>
                  {formatCad(getOrderTotal(order), currency)}
                </DetailLineValue>
              </DetailTotalLine>
            </DetailPanel>

            <DetailPanel>
              <DetailPanelTitle>
                <LocalShippingOutlinedIcon fontSize="inherit" />
                Shipping To
              </DetailPanelTitle>

              {shippingAddress ? (
                <>
                  {shippingAddress.name ? (
                    <DetailText>{shippingAddress.name}</DetailText>
                  ) : null}
                  {shippingAddress.company ? (
                    <DetailTextMuted>{shippingAddress.company}</DetailTextMuted>
                  ) : null}
                  {shippingAddress.street ? (
                    <DetailText>{shippingAddress.street}</DetailText>
                  ) : null}
                  {shippingAddress.cityLine ? (
                    <DetailText>{shippingAddress.cityLine}</DetailText>
                  ) : null}
                  {shippingAddress.phone ? (
                    <DetailTextMuted>{shippingAddress.phone}</DetailTextMuted>
                  ) : null}
                  {checkout.contact?.email ? (
                    <DetailTextMuted>{checkout.contact.email}</DetailTextMuted>
                  ) : null}
                </>
              ) : (
                <DetailTextMuted>No shipping address on file.</DetailTextMuted>
              )}
            </DetailPanel>

            <DetailPanel>
              <DetailPanelTitle>
                <CreditCardOutlinedIcon fontSize="inherit" />
                Payment
              </DetailPanelTitle>

              {cardDisplay ? (
                <DetailLine>
                  <DetailLineLabel>Card</DetailLineLabel>
                  <DetailLineValue>{cardDisplay}</DetailLineValue>
                </DetailLine>
              ) : null}

              {order?.charge?.id ? (
                <DetailLine>
                  <DetailLineLabel>Charge ID</DetailLineLabel>
                  <DetailLineValue
                    style={{ fontSize: 11, wordBreak: "break-all" }}
                  >
                    {order.charge.id}
                  </DetailLineValue>
                </DetailLine>
              ) : null}

              {receiptUrl ? (
                <ReceiptLink
                  href={receiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Stripe receipt
                  <OpenInNewIcon fontSize="inherit" />
                </ReceiptLink>
              ) : (
                <DetailTextMuted>No payment details available.</DetailTextMuted>
              )}
            </DetailPanel>
          </DetailsColumns>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailDialog;
